import { StandardTextFieldProps } from '@mui/material';
import React, { useMemo } from 'react';
import { TimePickerInputAdornment } from './TimePickerInputAdornment';
import { EDTextEdit } from '../../TextEdit/EDTextEdit';
import { CommonPropsAndTypes } from '../../_Types/StandardComponentProps';
import { getTryParseTime } from '../Utils/EDTimePickerUtils';
import { TEDFormatTime } from '../../../Utils/Formatters/FormatterClasses/TEDFormatTime';
import { IEditComponentProps } from '../../../Components/Attributes/EditableAttribute/EditComponentTypes';

export type TextEditPropsType = Omit<
  IEditComponentProps<any>,
  'value' | 'onChange' | 'onBlur' | 'classes'
>;
interface IProps extends StandardTextFieldProps {
  editValue: string | undefined;
  editOnChange?: (newValue: string, isValidDate: boolean) => void;
  editOnBlur?: (
    event: React.FocusEvent<HTMLInputElement | HTMLDivElement>,
    newValue: string,
    isValidDate: boolean
  ) => void;
  editOnFocus?: React.FocusEventHandler<HTMLElement>;
  buttonOnClick?: () => void;
  textEditProps: TextEditPropsType;
  adornmentPadding?: CommonPropsAndTypes.ExpandedSpacingType;
  fullscreenStillRenderTextEdit?: boolean;
  noChangeOnBlur?: boolean;
  noParsing?: boolean;
  parseParams?: string;
  inputGroupRef?: HTMLDivElement | null;
  inputGroupActive?: boolean;
  inputGroupGoNext?: () => void;
}

const timeFormatter = new TEDFormatTime();

export const TimePickerTextEdit: React.FC<
  React.PropsWithChildren<IProps>
> = pickerProps => {
  const editProps = pickerProps.textEditProps;

  const tryParseTime = useMemo(
    () => getTryParseTime(timeFormatter, pickerProps.parseParams),
    [pickerProps.parseParams]
  );

  return (
    <EDTextEdit
      {...editProps}
      value={pickerProps.editValue ?? ''}
      inputBaseRef={pickerProps.InputProps?.ref || undefined}
      onChange={newValue => {
        if (pickerProps.editOnChange) {
          pickerProps.editOnChange(newValue, Boolean(tryParseTime(newValue)));
        }
      }}
      onFocus={event => {
        if (pickerProps.editOnFocus) {
          pickerProps.editOnFocus(event);
        }
      }}
      onBlur={event => {
        const newValue = pickerProps.editValue;
        const parsedDate =
          pickerProps.noParsing || !newValue ? null : tryParseTime(newValue);
        if (pickerProps.editOnChange && !pickerProps.noChangeOnBlur) {
          pickerProps.editOnChange(
            parsedDate || newValue || '',
            Boolean(parsedDate)
          );
        }
        if (pickerProps.editOnBlur) {
          pickerProps.editOnBlur(
            event,
            parsedDate || newValue || '',
            Boolean(parsedDate)
          );
        }
      }}
      fullscreenStillRenderTextEdit={editProps.fullscreenStillRenderTextEdit}
      endAdornment={
        editProps.disabled ? undefined : (
          <TimePickerInputAdornment
            onClick={pickerProps.buttonOnClick}
            tooltip={editProps.tooltip === null ? undefined : editProps.tooltip}
            adornmentPadding={editProps.adornmentPadding}
          />
        )
      }
      disableInputGroup
      inputGroupActive={pickerProps.inputGroupActive}
      inputGroupGoNext={pickerProps.inputGroupGoNext}
      inputGroupRef={pickerProps.inputGroupRef}
    />
  );
};

export type { IProps as ITimePickerTextEditProps };
